import * as guiApi from '@/api/gui';
import { vibrationsOf, type ModeVector, type VibrationModeSet } from './types';

/**
 * Normal-mode plumbing.
 *
 * Two sources are supported, in this order:
 *   1. the platform's `/jsmol/*` endpoints, which already serve the vectors the
 *      JSmol iframe consumed (kept so existing job outputs behave identically);
 *   2. the modes parsed straight out of a Gaussian log by `format.gaussian`,
 *      which works offline and needs no backend round-trip.
 *
 * Both are normalised to a plain `Float32Array` of 3*N displacements in
 * angstrom, ready for `createVibrationFrames` and the arrow layer.
 */

const VECTOR_BLOCK = /^[+-]?[\d.]+(?:[eEdD][+-]?\d+)?$/;

function numericTokens(line: string): number[] {
    const parts = line.trim().split(/\s+/).filter((part) => part.length > 0);
    if (parts.length < 3) return [];
    if (!parts.every((part) => VECTOR_BLOCK.test(part))) return [];
    return parts.map((part) => Number.parseFloat(part.replace(/[dD](?=[+-])/, 'e')));
}

export interface JsmolVibrationPayload {
    /** Atom coordinates in angstrom, when the block carried them. */
    coords: Float32Array | null;
    /** Displacement vectors in angstrom. */
    vectors: Float32Array | null;
    atomCount: number;
}

/**
 * Parses the body of a JSmol `load DATA "vibration"` block. Accepts the two
 * layouts JSmol itself accepts: three numbers per atom (vectors only) or six
 * (coordinates followed by vectors), with optional count/title header lines.
 */
export function parseJsmolVibrationData(text: string): JsmolVibrationPayload {
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    const coords: number[] = [];
    const vectors: number[] = [];
    let width = 0;
    for (const line of lines) {
        const values = numericTokens(line);
        if (values.length === 0) continue;
        if (width === 0) width = values.length >= 6 ? 6 : 3;
        if (width === 6 && values.length >= 6) {
            coords.push(values[0], values[1], values[2]);
            vectors.push(values[3], values[4], values[5]);
        } else if (values.length >= 3) {
            vectors.push(values[0], values[1], values[2]);
        }
    }
    const atomCount = Math.floor(vectors.length / 3);
    return {
        coords: coords.length > 0 ? Float32Array.from(coords) : null,
        vectors: atomCount > 0 ? Float32Array.from(vectors) : null,
        atomCount
    };
}

export interface ArrowSegment {
    start: [number, number, number];
    end: [number, number, number];
}

/** Extracts `{x y z} {x y z}` pairs from JSmol `draw arrow` scripts. */
export function parseDrawArrowCommands(command: string): ArrowSegment[] {
    const segments: ArrowSegment[] = [];
    const scripts = command.split(/,|\n/);
    const pointPattern = /\{([^{}]*)\}/g;
    for (const script of scripts) {
        if (!/arrow/i.test(script)) continue;
        const points: Array<[number, number, number]> = [];
        pointPattern.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = pointPattern.exec(script)) !== null) {
            const values = match[1]
                .trim()
                .split(/\s+/)
                .map((part) => Number.parseFloat(part));
            if (values.length >= 3 && values.slice(0, 3).every(Number.isFinite)) {
                points.push([values[0], values[1], values[2]]);
            }
        }
        for (let i = 0; i + 1 < points.length; i += 2) {
            segments.push({ start: points[i], end: points[i + 1] });
        }
    }
    return segments;
}

/**
 * Builds a mode vector aligned with `reference` (the equilibrium coordinates of
 * the loaded structure). Draw-arrow scripts win because they carry absolute
 * positions, which removes any doubt about atom ordering.
 */
export function modeFromApiPayload(
    payload: { vibrationData?: string; arrowCommand?: string },
    reference: ArrayLike<number>
): ModeVector | null {
    const atomCount = Math.floor(reference.length / 3);
    if (atomCount === 0) return null;
    const mode = new Float32Array(atomCount * 3);

    const segments = payload.arrowCommand ? parseDrawArrowCommands(payload.arrowCommand) : [];
    if (segments.length > 0) {
        let matched = 0;
        for (const segment of segments) {
            let best = -1;
            let bestDistance = Number.POSITIVE_INFINITY;
            for (let i = 0; i < atomCount; i++) {
                const dx = reference[i * 3] - segment.start[0];
                const dy = reference[i * 3 + 1] - segment.start[1];
                const dz = reference[i * 3 + 2] - segment.start[2];
                const distance = dx * dx + dy * dy + dz * dz;
                if (distance < bestDistance) {
                    bestDistance = distance;
                    best = i;
                }
            }
            // Tolerate arrows that start slightly off the atom (JSmol offsets them).
            if (best < 0 || bestDistance > 4) continue;
            mode[best * 3] = segment.end[0] - segment.start[0];
            mode[best * 3 + 1] = segment.end[1] - segment.start[1];
            mode[best * 3 + 2] = segment.end[2] - segment.start[2];
            matched++;
        }
        if (matched > 0) return mode;
    }

    if (payload.vibrationData) {
        const parsed = parseJsmolVibrationData(payload.vibrationData);
        if (parsed.vectors) {
            const count = Math.min(atomCount, parsed.atomCount);
            for (let i = 0; i < count * 3; i++) mode[i] = parsed.vectors[i];
            return mode;
        }
    }
    return null;
}

/** Number of normal modes the backend reports for a path (0 when unavailable). */
export async function fetchVibrationLevels(path: string): Promise<number> {
    const response = await guiApi.getGUIOptions(path);
    if (!response?.ok || !response.data) return 0;
    return Math.max(0, Math.trunc(response.data.vibration?.levels ?? 0));
}

/** Fetches one mode from the platform API. `level` is 1-based, as the API expects. */
export async function fetchVibrationMode(
    path: string,
    level: number,
    reference: ArrayLike<number>
): Promise<{ mode: ModeVector | null; error?: string }> {
    const response = await guiApi.getVibrationData(path, level);
    if (!response?.ok || !response.data) {
        return { mode: null, error: response?.message };
    }
    const mode = modeFromApiPayload(response.data, reference);
    if (!mode) return { mode: null, error: 'vibration payload could not be parsed' };
    return { mode };
}

/** Modes embedded in the loaded structure (e.g. parsed from a Gaussian log). */
export function modesFromStructure(structure: Parameters<typeof vibrationsOf>[0]): VibrationModeSet | null {
    return vibrationsOf(structure);
}

/** RMS displacement of a mode, used to normalise visual amplitude. */
export function modeRms(mode: ArrayLike<number>): number {
    const atomCount = Math.floor(mode.length / 3);
    if (atomCount === 0) return 0;
    let sum = 0;
    for (let i = 0; i < mode.length; i++) sum += mode[i] * mode[i];
    return Math.sqrt(sum / atomCount);
}